/**
 * NP-70 / NP-416 — shared "start a new application" action.
 *
 * Detaches from the current loan and clears the per-application local state
 * so the wizard's create-once guard can create a fresh loan, then lands on
 * the dashboard to begin. Used by the loan switcher CTA (NP-70) and the
 * post-submission Contacts LO card (NP-416). LO inheritance for the new
 * loan is NP-415 (path_lo_email persists — it is NOT cleared here).
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoan } from '@/context/LoanContext';
import {
  clearApplicationDraft,
  clearApplicationDraftStash,
  updateApplicationDraft,
} from '@/lib/applicationDraft';
import { clearWizardApplication } from '@/lib/wizardApplicationStore';

export function useStartNewApplication(): () => void {
  const navigate = useNavigate();
  const { setActiveLoan } = useLoan();

  return useCallback(() => {
    clearWizardApplication();
    clearApplicationDraft();
    // NP-430: a deliberate fresh start must not be undone by the logout stash.
    clearApplicationDraftStash();
    // NP-424: the wizard save flow now resumes the borrower's existing
    // in-progress application when it has no loanId — mark that THIS flow
    // intends a brand-new loan so the create path runs instead. Cleared when
    // the create succeeds (the new loanId takes over).
    updateApplicationDraft((current) => ({ ...current, newLoanIntent: true }));
    setActiveLoan(undefined);
    navigate('/');
  }, [navigate, setActiveLoan]);
}
